// InvitationPage.jsx  — updated envelope & motion fixes
import React from "react";
import { motion } from "framer-motion";

const CONFETTI_COUNT = 40;

export default function InvitationPage() {
  const confetti = Array.from({ length: CONFETTI_COUNT });

  return (
    <>
      <style>{`
        :root { --pink-1:#ffd6ea; --pink-2:#ff9cc9; --paper:#fffafc; --card-shadow: rgba(6,10,20,0.18); }
        html,body,#root { height: 100%; margin:0; }
        .page { min-height: 100vh; width:100%; overflow:hidden; display:flex; align-items:center; justify-content:center;
               background: radial-gradient(circle at 10% 10%, rgba(255,230,250,0.18), transparent 10%),
                           radial-gradient(circle at 90% 20%, rgba(230,240,255,0.10), transparent 12%),
                           linear-gradient(135deg, #1b1024 0%, #3a3140 40%, #bfa8b8 100%);
               position:relative; color:#0b1020; font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial; }
        .confetti-layer{ position:fixed; inset:0; pointer-events:none; overflow:hidden; z-index:5; }
        .confetti-piece{ position:absolute; top:-8vh; width:8px; height:16px; border-radius:8px; opacity:0.95; box-shadow:0 2px 6px rgba(0,0,0,0.06); }
        .container{ width:min(980px,94vw); padding:40px; display:flex; gap:48px; align-items:center; justify-content:center; z-index:10; }
        .title-col{ flex:1 1 320px; color: rgba(255,245,255,0.95); }
        .small-note{ font-size:12px; letter-spacing:0.18em; opacity:0.9; margin-bottom:8px; }
        .headline{ font-size:clamp(26px,5vw,44px); margin:0 0 8px 0; font-weight:700; color:#ffeaff; text-shadow:0 2px 6px rgba(0,0,0,0.35); }
        .subtitle{ color: rgba(255,230,245,0.85); max-width:36ch; }

        /* envelope area */
        .envelope-wrapper { flex:0 0 420px; display:flex; align-items:center; justify-content:center; perspective:1400px; }
        .envelope-stage { width:min(420px,78vw); height:calc(min(420px,78vw) * 0.62); position:relative; transform-style:preserve-3d; display:flex; align-items:center; justify-content:center; }

        /* envelope elements: stable z-index values in CSS */
        .envelope-body {
          position:absolute; inset:auto 0 0 0; height:100%; margin:0 12px; border-radius:18px;
          background: linear-gradient(180deg,var(--pink-1),var(--pink-2));
          box-shadow: 0 14px 28px var(--card-shadow), 0 2px 6px rgba(0,0,0,0.08) inset;
          z-index: 6;
        }

        .envelope-flap {
          position:absolute; top: 0; left: 6%; right: 6%; height:70%; border-radius:12px;
          background: linear-gradient(180deg, #da92bcff, #ffb6dd);
          clip-path: polygon(0 0, 50% 72%, 100% 0);
          box-shadow: 0 10px 24px rgba(0,0,0,0.25);
          z-index: 8; /* above body */
          transform-origin: 50% 0%; /* top center */
          backface-visibility: hidden;
          transform-style: preserve-3d;
        }

        .envelope-flap2 {
          position:absolute; top: 0; left: 6%; right: 6%; height:70%; border-radius:12px;
          background: linear-gradient(180deg, #ffffffff, #ffb6dd);
          clip-path: polygon(0 0, 50% 72%, 100% 0);
          box-shadow: 0 10px 24px rgba(0,0,0,0.25);
          z-index: 7; /* above body */
          backface-visibility: hidden;
          transform-style: preserve-3d;
        }

        .letter {
          position:absolute; left:6%; right:6%; bottom:8%; height:76%; border-radius:12px;
          background: linear-gradient(180deg,#fffafc,#fff1fb);
          box-shadow: 0 16px 36px rgba(6,10,20,0.26);
          z-index: 9; /* slightly above flap so it appears in front */
          overflow:hidden;
          backface-visibility: hidden;
        }

        .letter-inner { padding:22px; }
        .letter-title { font-size:22px; margin:6px 0 2px 0; font-weight:700; color:#2b2730; }
        .letter-sub { font-size:15px; margin:6px 0 12px 0; color:#2f2a2f; font-weight:600; }
        .letter-body { font-size:13px; color:#4b454b; line-height:1.4; max-width:36ch; }

        .rsvp-pill{ display:inline-flex; gap:8px; align-items:center; padding:8px 12px; margin-top:14px; border-radius:999px; background:rgba(255,235,246,0.95); border:1px solid rgba(255,180,210,0.45); box-shadow:0 6px 18px rgba(6,10,20,0.12); font-weight:600; font-size:13px; }

        @media (max-width:720px){ .container{ gap:20px; padding:28px; flex-direction:column; align-items:center; } .envelope-wrapper{ order:-1; } }
      `}</style>

      <div className="page">
        <div className="confetti-layer" aria-hidden>
          {confetti.map((_, i) => {
            const left = Math.random() * 100;
            const delay = Math.random() * 3;
            const dur = 3.5 + Math.random() * 2;
            const colors = [
              "linear-gradient(180deg,#ffe6ff,#ff8ac7)",
              "linear-gradient(180deg,#e6f4ff,#9fd8ff)",
              "linear-gradient(180deg,#fff1d6,#ffc86b)",
              "linear-gradient(180deg,#ffdff3,#ffb6e0)"
            ];
            const bg = colors[i % colors.length];
            const style = { left: `${left}%`, animation: `fall-${i} ${dur}s linear ${delay}s infinite`, background: bg, transform: `rotate(${Math.random() * 360}deg)` };
            return <span key={i} className="confetti-piece" style={style} />;
          })}
        </div>

        <div className="container" role="main">
          <div className="title-col">
            <div className="small-note">✧ Exclusive Invitation ✧</div>
            <h1 className="headline">CSB Study Soirée</h1>
            <p className="subtitle">Join us for an <strong>elegant</strong> deep dive into arrays, trees, graphs, and more.</p>
          </div>

          <div className="envelope-wrapper">
            <motion.div
              className="envelope-stage"
              initial={{ scale: 0.94, rotateX: -6, opacity: 0 }}
              animate={{ scale: 1, rotateX: 0, opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.7, type: "spring", stiffness: 70 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              {/* envelope body (stable) */}
              <motion.div
                className="envelope-body"
                initial={{ y: 18, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.7 }}
              />

              {/* flap: rotate OPEN by -140deg — no z animation */}
              <motion.div
                className="envelope-flap"
                initial={{ rotateX: 0 }}
                animate={{ rotateX: -140 }}
                transition={{
                  delay: 1.25,
                  duration: 1.95,
                  type: "spring",
                  stiffness: 48,
                  damping: 12,
                }}
                style={{ willChange: "transform" }}
              />

              <div className="envelope-flap2" />

              {/* letter: slide out gently — KEEP scale at 1, DO NOT animate z */}
              <motion.div
                className="letter"
                initial={{ y: 60, opacity: 0, scale: 1 }}
                animate={{ y: -18, opacity: 1, scale: 1 }}
                transition={{ delay: 1.9, duration: 0.85, type: "spring", stiffness: 82, damping: 14 }}
                style={{ willChange: "transform, opacity" }}
              >
                <div className="letter-inner">
                  <div style={{ fontSize: 12, color: "#6f6470", letterSpacing: "0.04em", marginBottom: 6 }}>
                    You are cordially invited
                  </div>

                  <div className="letter-title">2025 CSB</div>
                  <div className="letter-sub">Data Structures Exclusive Study Group</div>

                  <div className="letter-body">
                    Bring your favorite data structure
                    <div className="rsvp-pill" style={{ marginTop: 10 }}>
                      <span style={{ width:8, height:8, borderRadius:8, background:"#ff77b7", display:"inline-block" }} />
                      Sunday 12/07, 1:00 PM - 11:59 PM
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* per-piece confetti keyframes as before */}
      <style>{`
        ${confetti.map((_, i) => {
          const xOff = (Math.random() * 40 - 20).toFixed(1);
          return `
            @keyframes fall-${i} {
              0% { transform: translate3d(${xOff}px, -12vh, 0) rotate(${(i*47)%360}deg); opacity:0; }
              10% { opacity:1; }
              100% { transform: translate3d(${xOff}px, 120vh, 0) rotate(${(i*720)%360}deg); opacity:0; }
            }
          `;
        }).join("\n")}
      `}</style>
    </>
  );
}
